import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  generateMaze,
  isPieceClearable,
  isMazeSolved,
  GeneratedMaze,
} from '../utils/mazeGenerator';
import { LEVEL_CONFIGS } from '../types/maze';
import { getCookie, setCookie, clearGameCookies } from '../utils/cookies';
import MazePieceLayer, { ExitingPiece } from '../components/MazePieceLayer';

const MAX_HEARTS = 3;

const Game: React.FC = () => {
  const { level: levelParam } = useParams<{ level: string }>();
  const navigate = useNavigate();
  const level = Number(levelParam);
  const config = LEVEL_CONFIGS.find((l) => l.level === level);

  const [maze, setMaze] = useState<GeneratedMaze | null>(null);
  const [hearts, setHearts] = useState<number>(() => {
    const saved = getCookie('gameHearts');
    return saved !== null ? Number(saved) : MAX_HEARTS;
  });
  const [status, setStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [hintPieceId, setHintPieceId] = useState<number | null>(null);
  const [shakePieceId, setShakePieceId] = useState<number | null>(null);
  const [exitingPiece, setExitingPiece] = useState<ExitingPiece | null>(null);

  useEffect(() => {
  if (!getCookie('playerName') || !getCookie('playerPhone')) {
    navigate('/');
    return;
  }
  if (!config) {
    navigate('/levels');
    return;
  }
  setMaze(generateMaze(config.rows, config.cols, config.maxPieceLen));
  setStatus('playing');
  setHintPieceId(null);
  setExitingPiece(null);
}, [level, config, navigate]);


  useEffect(() => {
    setCookie('gameHearts', String(hearts));
  }, [hearts]);

  useEffect(() => {
    if (status === 'won') {
      const timer = setTimeout(() => {
        if (level < 9) {
          navigate(`/game/${level + 1}`);
        } else {
          navigate('/');
        }
      }, 1800);
      return () => clearTimeout(timer);
    }
    if (status === 'lost') {
      const timer = setTimeout(async () => {
        await persistProgress();
        setCookie('gameHearts', String(MAX_HEARTS));
        navigate('/');
      }, 2200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleCellClick = (row: number, col: number) => {
    if (status !== 'playing' || !maze || exitingPiece) return;
    const pid = maze.cellPieceId[row][col];
    if (pid === -1) return;
    const piece = maze.pieces[pid];
    if (!piece.active) return;

    if (isPieceClearable(maze, piece)) {
      setExitingPiece({ id: pid, moving: false });
      requestAnimationFrame(() => {
        setExitingPiece({ id: pid, moving: true });
      });
    } else {
      setShakePieceId(pid);
      setTimeout(() => setShakePieceId(null), 300);
      setHearts((h) => {
        const remaining = h - 1;
        if (remaining <= 0) setStatus('lost');
        return remaining;
      });
    }
  };

  const saveCompletion = async () => {
    const name = getCookie('playerName');
    const phone = getCookie('playerPhone');
    if (!name || !phone) return;

    const currentUnlocked = Number(getCookie('unlockedLevel') || '1');
    const newUnlocked = Math.min(9, Math.max(currentUnlocked, level + 1));
    setCookie('unlockedLevel', String(newUnlocked));

    try {
      await setDoc(
        doc(db, 'users', phone),
        {
          name,
          phone,
          unlockedLevel: newUnlocked,
          [`completedLevels.${level}`]: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  };

  const persistProgress = async () => {
    const name = getCookie('playerName');
    const phone = getCookie('playerPhone');
    if (!name || !phone) return;

    const unlocked = Number(getCookie('unlockedLevel') || '1');
    try {
      await setDoc(
        doc(db, 'users', phone),
        { name, phone, unlockedLevel: unlocked, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  };

  const handleExitComplete = (id: number) => {
    setMaze((prev) => {
      if (!prev) return prev;
      const nextPieces = prev.pieces.map((p) => (p.id === id ? { ...p, active: false } : p));
      const nextMaze = { ...prev, pieces: nextPieces };
      if (isMazeSolved(nextMaze)) {
        setStatus('won');
        saveCompletion();
      }
      return nextMaze;
    });
    setExitingPiece(null);
    setHintPieceId(null);
  };

  const handleSaveAndExit = async () => {
    await persistProgress();
    clearGameCookies();
    navigate('/');
  };

  if (!config || !maze) return null;

 
const maxBoardSize = Math.min(560, Math.max(320, Math.max(maze.rows, maze.cols) * 42));
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-500 to-indigo-600 flex flex-col items-center justify-center px-3 py-4">
      
      <div className="w-full max-w-md flex justify-center mb-3">
        <img 
          src="/images/logo.png"   
          alt="Amaze Go Logo" 
          className="w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-lg rounded-2xl" 
        />
      </div>

      <div className="w-full max-w-md flex items-center justify-between mb-4">
        <button onClick={() => navigate('/levels')} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center text-2xl hover:bg-white/30 transition">
          ‹
        </button>
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-white/80 mr-1">Lives:</span>
          {Array.from({ length: MAX_HEARTS }).map((_, i) => (
            <span key={i} className={`text-xl ${i < hearts ? 'text-rose-400' : 'text-white/30'}`}>♥</span>
          ))}
        </div>
      </div>

      <div className="w-full max-w-md flex justify-between items-center mb-3">
        <p className="text-white/80 text-sm font-medium">Level {level}</p>
        <button onClick={handleSaveAndExit} className="px-4 py-2 rounded-full bg-white text-indigo-600 text-sm font-semibold hover:bg-white/90 transition shadow-md">
          Save & Exit
        </button>
      </div>

      <div 
        className="relative w-full bg-white rounded-xl shadow-2xl p-0" 
        style={{ maxWidth: maxBoardSize, aspectRatio: `${maze.cols} / ${maze.rows}` }}
      >
        <MazePieceLayer 
          maze={maze}
          hintPieceId={hintPieceId}
          shakePieceId={shakePieceId}
          exitingPiece={exitingPiece}
          onExitComplete={handleExitComplete}
        />
        <div
          className="absolute inset-0 grid"
          style={{ gridTemplateColumns: `repeat(${maze.cols}, 1fr)`, gridTemplateRows: `repeat(${maze.rows}, 1fr)` }}
        >
          {Array.from({ length: maze.rows }).flatMap((_, r) =>
            Array.from({ length: maze.cols }).map((_, c) => (
              <button 
                key={`${r}-${c}`} 
                onClick={() => handleCellClick(r, c)} 
                className="w-full h-full" 
                style={{ background: 'transparent' }} 
              />
            ))
          )}
        </div>
      </div>

      {status === 'won' && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 text-center max-w-xs w-full">
            <p className="text-2xl font-bold text-gray-800 mb-2">Level Complete! 🎉</p>
            <p className="text-gray-500 text-sm">
              {level < 9 ? 'Moving to the next level...' : 'You beat every level! Heading home...'}
            </p>
          </div>
        </div>
      )}

      {status === 'lost' && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 text-center max-w-xs w-full">
            <p className="text-2xl font-bold text-gray-800 mb-2">Out of Hearts 💔</p>
            <p className="text-gray-500 text-sm">Saving your progress and heading home...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Game;